import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Send, MessageCircle, CalendarDays, Pencil, X, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import VoiceButton from "@/components/VoiceButton";

const inputCls = "w-full h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]";

const STATUS_CHIP = {
  available: { label: "Available", cls: "bg-emerald-50 text-[#007F3B]" },
  unavailable: { label: "Not available", cls: "bg-red-50 text-[#D5281B]" },
  pending: { label: "Awaiting reply", cls: "bg-slate-100 text-slate-500" },
};

const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); } catch { return d; }
};

export default function AdminBroadcast() {
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [events, setEvents] = useState([]);
  const [offers, setOffers] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [eventId, setEventId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [rate, setRate] = useState("");
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);
  const [editOffer, setEditOffer] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: s }, { data: e }, { data: o }] = await Promise.all([
        api.get("/crm/staff-contacts"), api.get("/events"), api.get("/crm/shift-offers"),
      ]);
      setStaff(s);
      setSelectedStaff(s.map((x) => x.id));
      setEvents(e.filter((ev) => !ev.archived));
      setOffers(o);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const linkEvent = (id) => {
    setEventId(id);
    if (!id) return;
    const ev = events.find((e) => e.id === id);
    if (!ev) return;
    setDate(ev.event_date || "");
    const times = (ev.times || "").split(/[-–—]/).map((t) => t.trim());
    setTime(times[0] || "");
    const bits = [`Shift available: ${ev.name}`];
    if (ev.event_date) bits.push(`Date: ${ev.event_date}`);
    if (ev.times) bits.push(`Times: ${ev.times}`);
    if (ev.venue) bits.push(`Venue: ${ev.venue}`);
    setMessage(bits.join("\n"));
    if (!subject) setSubject(`Shift available — ${ev.name}`);
  };

  const fullMessage = () => {
    const parts = [message.trim()];
    const when = [date && `Date: ${date}`, time && `Time: ${time}`].filter(Boolean).join(" · ");
    if (when && !message.includes(date || "\0")) parts.push(when);
    if (rate.trim()) parts.push(`Rate: ${rate.trim()}`);
    return parts.filter(Boolean).join("\n");
  };

  const waLink = (phone) => {
    let p = (phone || "").replace(/\D/g, "");
    if (p.startsWith("0")) p = "44" + p.slice(1);
    return `https://wa.me/${p}?text=${encodeURIComponent(fullMessage())}`;
  };

  const waRespLink = (phone, o, rec) => {
    let p = (phone || "").replace(/\D/g, "");
    if (p.startsWith("0")) p = "44" + p.slice(1);
    const origin = window.location.origin;
    const bits = [o.message];
    const when = [o.date && `Date: ${o.date}`, o.time && `Time: ${o.time}`].filter(Boolean).join(" · ");
    if (when) bits.push(when);
    if (o.rate) bits.push(`Rate: ${o.rate}`);
    bits.push("", "Can you do this shift? Tap one:",
      `I'm available: ${origin}/api/public/shift-response/${rec.token}?r=available`,
      `Sorry, I'm not available: ${origin}/api/public/shift-response/${rec.token}?r=unavailable`);
    return `https://wa.me/${p}?text=${encodeURIComponent(bits.join("\n"))}`;
  };

  const toggleStaff = (id) =>
    setSelectedStaff((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));

  const sendBroadcast = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/crm/broadcast", {
        user_ids: selectedStaff, subject, message: message.trim(), date, time, rate: rate.trim(), event_id: eventId,
      });
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      const { data } = await api.patch(`/crm/shift-offers/${editOffer.id}`, {
        subject: editOffer.subject, message: editOffer.message,
        date: editOffer.date, time: editOffer.time, rate: editOffer.rate,
      });
      setOffers((os) => os.map((o) => (o.id === data.id ? data : o)));
      setEditOffer(null);
      toast.success("Shift offer updated — replies staff already sent are kept");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEditSaving(false);
    }
  };

  const staffPhone = (userId) => (staff || []).find((s) => s.id === userId)?.phone || "";

  useEffect(() => {
    if (!date) return;
    api.get(`/crm/staff-contacts?date=${date}`).then((r) => setStaff(r.data)).catch((e) => console.debug("Availability refresh failed:", e?.message || e));
  }, [date]);

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="broadcast-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate("/admin")} data-testid="broadcast-back"
            className="h-11 w-11 grid place-items-center rounded-lg hover:bg-slate-100 transition-colors">
            <ChevronLeft className="h-5 w-5 text-[#0F2547]" />
          </button>
          <div>
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-[#0F2547]">Send a shift to staff</h1>
            <p className="text-xs text-slate-500">Staff tap Available / Not available straight from the email or WhatsApp — replies show below</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4">
        <section className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
          <label className="block">
            <span className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5">
              <CalendarDays className="h-3.5 w-3.5" /> Link to a calendar event (optional — fills everything in for you)
            </span>
            <select value={eventId} onChange={(e) => linkEvent(e.target.value)} data-testid="broadcast-event"
              className={`${inputCls} bg-white`}>
              <option value="">— No event, I'll type it myself —</option>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>{ev.name}{ev.event_date ? ` — ${ev.event_date}` : ""}</option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="broadcast-date"
              title="Shift date" className={`${inputCls} bg-white`} />
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="broadcast-time"
              title="Start time" className={`${inputCls} bg-white`} />
          </div>
          <input placeholder="Rate of pay (optional — e.g. £180 for the shift, or £15/hr)" value={rate}
            onChange={(e) => setRate(e.target.value)} data-testid="broadcast-rate" className={inputCls} />
          <div className="flex items-start gap-2">
            <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)}
              data-testid="broadcast-message"
              placeholder="e.g. Boxing cover needed Saturday 27th, Newport Leisure Centre, 6pm–11pm. Who's free?"
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            <div className="pt-1.5">
              <VoiceButton testid="broadcast-voice"
                onTranscript={(t) => setMessage((m) => (m ? m + " " : "") + t)} />
            </div>
          </div>
          <input placeholder="Email subject (optional)" value={subject}
            onChange={(e) => setSubject(e.target.value)} data-testid="broadcast-subject" className={inputCls} />
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-base font-bold text-[#0F2547]">Who's it going to?</h2>
            <button onClick={() => setSelectedStaff(selectedStaff.length === staff?.length ? [] : (staff || []).map((x) => x.id))}
              data-testid="broadcast-toggle-all"
              className="text-xs font-bold text-[#005EB8] hover:underline">
              {selectedStaff.length === staff?.length ? "Untick all" : "Tick all"}
            </button>
          </div>
          <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 max-h-64 overflow-y-auto" data-testid="broadcast-staff-list">
            {staff === null ? (
              <p className="text-xs text-slate-400 px-3 py-3">Loading staff…</p>
            ) : staff.length === 0 ? (
              <p className="text-xs text-slate-400 px-3 py-3">No active staff found.</p>
            ) : staff.map((s) => (
              <div key={s.id} className="flex items-center gap-2 px-3 py-2.5" data-testid={`broadcast-row-${s.id}`}>
                <label className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer">
                  <input type="checkbox" checked={selectedStaff.includes(s.id)} onChange={() => toggleStaff(s.id)}
                    data-testid={`broadcast-tick-${s.id}`} className="h-5 w-5 accent-[#005EB8]" />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-slate-700 truncate">{s.name}</span>
                    <span className="block text-[11px] text-slate-400 truncate">{s.email}</span>
                    {s.availability === "available" && <span className="block text-[10px] font-bold text-[#007F3B]" data-testid={`broadcast-avail-${s.id}`}>Available that day</span>}
                    {s.availability === "unavailable" && <span className="block text-[10px] font-bold text-[#D5281B]" data-testid={`broadcast-avail-${s.id}`}>Not available that day</span>}
                  </span>
                </label>
                {s.phone ? (
                  <a href={waLink(s.phone)} target="_blank" rel="noreferrer" data-testid={`broadcast-wa-${s.id}`}
                    className="h-9 px-3 rounded-lg bg-[#25D366] hover:bg-[#1fb857] transition-colors text-white text-xs font-bold inline-flex items-center gap-1.5 shrink-0">
                    <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                  </a>
                ) : (
                  <span className="text-[10px] text-slate-300 shrink-0">no mobile saved</span>
                )}
              </div>
            ))}
          </div>
          <button onClick={sendBroadcast} disabled={sending || !message.trim() || selectedStaff.length === 0}
            data-testid="broadcast-send"
            className="w-full h-12 rounded-xl bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 transition-colors text-white text-sm font-bold inline-flex items-center justify-center gap-2">
            <Send className="h-4 w-4" />
            {sending ? "Sending…" : `Email ${selectedStaff.length || ""} selected staff member${selectedStaff.length === 1 ? "" : "s"}`}
          </button>
          <p className="text-[11px] text-slate-400">
            Everyone emailed gets <strong>I'm available</strong> / <strong>I'm not available</strong> buttons — their answer appears in the list below.
            WhatsApp buttons open your own WhatsApp with the message ready (use the ones in the list below for personal reply links).
          </p>
        </section>

        {offers.length > 0 && (
          <section className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3" data-testid="offers-board">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-base font-bold text-[#0F2547]">Shift offers &amp; replies</h2>
            </div>
            {offers.map((o) => {
              const counts = { available: 0, unavailable: 0, pending: 0 };
              o.recipients.forEach((r) => { counts[r.status] = (counts[r.status] || 0) + 1; });
              const editing = editOffer?.id === o.id;
              return (
                <div key={o.id} className="rounded-xl border border-slate-200 p-3.5 space-y-2.5" data-testid={`offer-card-${o.id}`}>
                  {editing ? (
                    <form onSubmit={saveEdit} className="space-y-2" data-testid={`offer-edit-form-${o.id}`}>
                      <input value={editOffer.subject} onChange={(e) => setEditOffer((f) => ({ ...f, subject: e.target.value }))}
                        placeholder="Subject" data-testid={`offer-edit-subject-${o.id}`} className={inputCls} />
                      <div className="grid grid-cols-3 gap-2">
                        <input type="date" value={editOffer.date} onChange={(e) => setEditOffer((f) => ({ ...f, date: e.target.value }))}
                          data-testid={`offer-edit-date-${o.id}`} className={`${inputCls} bg-white`} />
                        <input type="time" value={editOffer.time} onChange={(e) => setEditOffer((f) => ({ ...f, time: e.target.value }))}
                          data-testid={`offer-edit-time-${o.id}`} className={`${inputCls} bg-white`} />
                        <input value={editOffer.rate} onChange={(e) => setEditOffer((f) => ({ ...f, rate: e.target.value }))}
                          placeholder="Rate e.g. £180" data-testid={`offer-edit-rate-${o.id}`} className={inputCls} />
                      </div>
                      <textarea rows={3} value={editOffer.message} onChange={(e) => setEditOffer((f) => ({ ...f, message: e.target.value }))}
                        data-testid={`offer-edit-message-${o.id}`}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                      <div className="flex gap-2">
                        <button type="submit" disabled={editSaving} data-testid={`offer-edit-save-${o.id}`}
                          className="h-10 px-4 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-60 text-white text-xs font-bold inline-flex items-center gap-1.5">
                          <Check className="h-3.5 w-3.5" /> {editSaving ? "Saving…" : "Save changes"}
                        </button>
                        <button type="button" onClick={() => setEditOffer(null)} data-testid={`offer-edit-cancel-${o.id}`}
                          className="h-10 px-4 rounded-lg border border-slate-300 text-slate-600 text-xs font-bold inline-flex items-center gap-1.5">
                          <X className="h-3.5 w-3.5" /> Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-[#0F2547] truncate">{o.subject || "Shift offer"}</p>
                          <p className="text-[11px] text-slate-500">
                            {[fmtDate(o.date), o.time, o.rate].filter(Boolean).join(" · ") || "No date set"} · sent {new Date(o.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                          </p>
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                          <button onClick={() => setEditOffer({ id: o.id, subject: o.subject, message: o.message, date: o.date, time: o.time, rate: o.rate })}
                            data-testid={`offer-edit-${o.id}`} title="Edit this shift offer (price, date, message)"
                            className="h-9 w-9 grid place-items-center rounded-lg border border-slate-300 text-slate-500 hover:text-[#005EB8] hover:border-[#005EB8] transition-colors">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={async () => {
                              if (!window.confirm("Delete this shift offer and all its replies?")) return;
                              try {
                                await api.delete(`/crm/shift-offers/${o.id}`);
                                setOffers((os) => os.filter((x) => x.id !== o.id));
                                toast.success("Shift offer deleted");
                              } catch (err) { toast.error(formatApiError(err)); }
                            }} data-testid={`offer-delete-${o.id}`} title="Delete this shift offer"
                            className="h-9 w-9 grid place-items-center rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 transition-colors">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                      <p className="text-[11px] font-bold text-slate-500">
                        <span className="text-[#007F3B]">{counts.available} available</span> ·{" "}
                        <span className="text-[#D5281B]">{counts.unavailable} not available</span> ·{" "}
                        <span>{counts.pending} awaiting reply</span>
                      </p>
                      <div className="divide-y divide-slate-100">
                        {o.recipients.map((r) => {
                          const chip = STATUS_CHIP[r.status] || STATUS_CHIP.pending;
                          const phone = staffPhone(r.user_id);
                          return (
                            <div key={r.user_id} className="flex items-center gap-2 py-2" data-testid={`offer-recipient-${o.id}-${r.user_id}`}>
                              <span className="flex-1 min-w-0">
                                <span className="block text-sm font-semibold text-slate-700 truncate">{r.name}</span>
                                {r.responded_at && (
                                  <span className="block text-[10px] text-slate-400">
                                    replied {new Date(r.responded_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                                  </span>
                                )}
                              </span>
                              <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${chip.cls}`}
                                data-testid={`offer-status-${o.id}-${r.user_id}`}>{chip.label}</span>
                              {r.booked && (
                                <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-800"
                                  data-testid={`offer-booked-${o.id}-${r.user_id}`}>On rota</span>
                              )}
                              {phone && (
                                <a href={waRespLink(phone, o, r)} target="_blank" rel="noreferrer"
                                  data-testid={`offer-wa-${o.id}-${r.user_id}`} title="WhatsApp them with their personal reply links"
                                  className="h-8 w-8 grid place-items-center rounded-lg bg-[#25D366] hover:bg-[#1fb857] text-white transition-colors shrink-0">
                                  <MessageCircle className="h-3.5 w-3.5" />
                                </a>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </section>
        )}
      </main>
    </div>
  );
}
